import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import {
    closeCreateModal,
    fetchCreateUserInvitation,
    selectCreateUserInvitationOnCreate,
    selectIsCreateUserInvitationModalOpen
} from "../../model"
import { SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import notify from "@/shared/lib/notify"
import toast from "react-hot-toast"
import { Modal } from "@/shared/ui/modal"
import { FormInput } from "@/shared/ui/form-input"
import { selectUsersGroups } from "@/entities/groups"
import { useMemo } from "react"
import { getErrorMessage } from "@/shared/consts/error-codes"

const schema = yup.object({
    first_name: yup.string().required('Введите имя'),
    last_name: yup.string().required('Введите фамилию'),
    phone: yup
        .string()
        .required('Введите номер телефона')
        .matches(/^\+\d{11,15}$/, 'Формат: +71234567890'),
    email: yup.string().email('Некорректный email').optional(),
    group_id: yup.string().optional(),
})

type FormValues = yup.InferType<typeof schema>

const defaultValues: FormValues = {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    group_id: '',
}

export const CreateUserInvitationModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsCreateUserInvitationModalOpen)
    const onCreate = useAppSelector(selectCreateUserInvitationOnCreate)
    const usersGroups = useAppSelector(selectUsersGroups)

    const groupOptions = useMemo(
        () => usersGroups.map((g) => ({ value: g.id, label: g.name })),
        [usersGroups]
    )

    const handleClose = () => {
        dispatch(closeCreateModal())
    }

    const {
        handleSubmit,
        control,
        register,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<FormValues>({
        defaultValues,
        resolver: yupResolver(schema),
    })

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        await new Promise<void>((res, rej) => {
            const payload = {
                first_name: data.first_name,
                last_name: data.last_name,
                phone: data.phone,
                ...(data.email ? { email: data.email } : {}),
                ...(data.group_id ? { group_id: data.group_id } : {}),
            }
            dispatch(fetchCreateUserInvitation(payload))
                .unwrap()
                .then(({ already_in_community }) => {
                    if (already_in_community) {
                        toast('Пользователь уже в сообществе')
                    } else {
                        notify.success('Пользователь приглашён')
                    }
                    reset()
                    onCreate?.()
                    handleClose()
                    res()
                })
                .catch((e) => {
                    notify.error(getErrorMessage(e))
                    rej()
                })
        })
    }

    return (
        <Modal isOpen={isOpen} title="Создать пользователя" onRequestClose={handleClose}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <FormInput
                    register={register}
                    label="Имя"
                    id="first_name"
                    error={errors.first_name}
                    required
                    placeholder="Иван"
                />
                <FormInput
                    register={register}
                    label="Фамилия"
                    id="last_name"
                    error={errors.last_name}
                    required
                    placeholder="Иванов"
                />
                <FormInput
                    register={register}
                    label="Телефон"
                    id="phone"
                    error={errors.phone}
                    required
                    placeholder="+71234567890"
                />
                <FormInput
                    register={register}
                    label="Email"
                    id="email"
                    error={errors.email}
                    required={false}
                    placeholder="ivan@example.com"
                />
                <FormInput
                    label="Группа"
                    id="group_id"
                    error={errors.group_id}
                    options={groupOptions}
                    control={control}
                    type="select"
                    required={false}
                    placeholder="Выберите группу"
                />
                <div className="w-full flex gap-3">
                    <button type="button" onClick={handleClose} className="form_cancel_button">
                        Отменить
                    </button>
                    <button type="submit" disabled={isSubmitting} className="form_handle_button">
                        Создать
                    </button>
                </div>
            </form>
        </Modal>
    )
}
